<template>
    <AdmModal :title="`Solicitud #${order?.id ?? orderId}`" size="lg" persistent @close="$emit('close')">
        <p v-if="loading" class="adm-muted">Cargando…</p>

        <div v-else-if="order" class="detalle">
            <section class="detalle__section">
                <h4 class="detalle__heading">Tipo de solicitud</h4>
                <AdmBadge :tone="typeMeta.tone" :label="requestTypeLabel(order.requestType)" />
            </section>

            <section v-if="order.product" class="detalle__section">
                <h4 class="detalle__heading">Producto solicitado</h4>
                <div class="detalle__product">
                    <img v-if="order.product.imageUrl" :src="order.product.imageUrl" :alt="order.product.name" />
                    <div>
                        <p><strong>#{{ order.product.id }} — {{ order.product.name }}</strong></p>
                        <p>Precio de catálogo: {{ formatCurrency(order.product.priceFrom) }}</p>
                    </div>
                </div>
            </section>

            <section class="detalle__section">
                <h4 class="detalle__heading">Datos del usuario</h4>
                <p><strong>Nombre:</strong> {{ order.contactName }}</p>
                <p><strong>Email:</strong> <a :href="`mailto:${order.contactEmail}`">{{ order.contactEmail }}</a></p>
                <p v-if="order.contactPhone"><strong>Teléfono:</strong> {{ order.contactPhone }}</p>
            </section>

            <section v-if="order.description" class="detalle__section">
                <h4 class="detalle__heading">Descripción</h4>
                <p class="detalle__description">{{ order.description }}</p>
            </section>

            <section v-if="order.dimensionsMm || order.volumeMm3 || order.materialId" class="detalle__section">
                <h4 class="detalle__heading">Especificaciones</h4>
                <p v-if="order.dimensionsMm">
                    <strong>Dimensiones:</strong>
                    {{ order.dimensionsMm.x }} × {{ order.dimensionsMm.y }} × {{ order.dimensionsMm.z }} mm
                </p>
                <p v-if="order.volumeMm3">
                    <strong>Volumen:</strong> {{ formatNumber(order.volumeMm3) }} mm³
                    ({{ formatNumber(order.volumeMm3 / 1000, 2) }} cm³)
                </p>
                <p v-if="order.material">
                    <strong>Material seleccionado:</strong> #{{ order.material.id }} — {{ order.material.name }}
                </p>
                <p v-if="order.quantity"><strong>Cantidad:</strong> {{ order.quantity }}</p>
            </section>

            <section v-if="order.pricing" class="detalle__section detalle__section--pricing">
                <h4 class="detalle__heading">Cálculo automático</h4>
                <p>Costo por gramo: <strong>{{ formatCurrency(order.pricing.costPerG) }}</strong></p>
                <p v-if="order.material">
                    Infill aplicado: <strong>{{ formatNumber(order.material.printDensityPercent ?? 100, 3) }} %</strong>
                </p>
                <p>Peso estimado: <strong>{{ formatNumber(order.pricing.weightG, 3) }} g</strong></p>
                <p>Costo base: <strong>{{ formatCurrency(order.pricing.costBase) }}</strong></p>
                <p :class="{ 'detalle__surcharge': order.pricing.overThreshold }">
                    Ganancia aplicada:
                    <strong>{{ formatNumber(order.pricing.profitAppliedPercent, 3) }} %</strong>
                    <span v-if="order.pricing.overThreshold">— ganancia para volumen grande (vol ≥ umbral)</span>
                </p>
                <p class="detalle__final">
                    Precio FINAL: <strong>{{ formatCurrency(order.pricing.finalPrice) }}</strong>
                </p>
            </section>

            <!-- Pedido personalizado: qué eligió el cliente y cómo se ve. -->
            <section v-if="order.customization" class="detalle__section">
                <h4 class="detalle__heading">Personalización</h4>
                <div class="detalle__custom">
                    <figure v-if="previewUrl" class="detalle__preview">
                        <img :src="previewUrl" alt="Previsualización confirmada por el cliente" />
                        <figcaption class="adm-muted">Vista previa confirmada por el cliente</figcaption>
                    </figure>
                    <ul class="detalle__custom-data">
                        <li><strong>Producto:</strong> {{ productTypeLabel(order.customization.productType) }}</li>
                        <li><strong>Tamaño:</strong> {{ sizeLabel(order.customization.sizeId) }}</li>
                        <li v-if="order.customization.pieces">
                            <strong>Piezas:</strong> {{ order.customization.pieces }}
                        </li>
                        <li><strong>Estuche / acabado:</strong> {{ caseLabel(order.customization.caseOption) }}</li>
                        <li>
                            <strong>Texto a personalizar:</strong>
                            <span v-if="order.customization.personalizationText">
                                «{{ order.customization.personalizationText }}»
                            </span>
                            <span v-else>—</span>
                        </li>
                        <!-- Parámetros del estudio V2. Ausentes en los
                             pedidos anteriores al 26/09/2026. -->
                        <template v-if="order.customization.seed">
                            <li>
                                <strong>Rejilla:</strong>
                                {{ order.customization.grid.cols }} × {{ order.customization.grid.rows }}
                                ({{ order.customization.orientation === 'portrait' ? 'vertical' : 'horizontal' }})
                            </li>
                            <li>
                                <strong>Formato:</strong>
                                {{ order.customization.sheetMm.w }} × {{ order.customization.sheetMm.h }} mm ·
                                holgura {{ order.customization.clearanceMm }} mm ·
                                grosor {{ order.customization.thicknessMm }} mm
                            </li>
                            <li>
                                <strong>Edición:</strong>
                                umbral {{ order.customization.threshold }} ·
                                simplificación {{ order.customization.simplifyMm }} mm ·
                                {{ order.customization.invert ? 'invertida' : 'sin invertir' }} ·
                                {{ order.customization.renderDpi }} dpi
                            </li>
                            <li>
                                <strong>Semilla del corte:</strong>
                                <code>{{ order.customization.seed }}</code>
                                <small> — con ella el troquel es reproducible</small>
                            </li>
                        </template>
                    </ul>
                </div>
            </section>

            <!-- Archivos listos para producción (plan 26_09_2026, R8). -->
            <section v-if="artifacts.length" class="detalle__section">
                <h4 class="detalle__heading">Archivos de producción</h4>
                <ul class="detalle__artifacts">
                    <li v-for="artifact in artifacts" :key="artifact.kind">
                        <div class="detalle__artifact-info">
                            <strong>{{ artifact.label }}</strong>
                            <small class="adm-muted">{{ artifact.detail }}</small>
                        </div>
                        <AdmButton size="sm" :icon="Download" @click="onDownloadArtifact(artifact.kind)">
                            Descargar
                        </AdmButton>
                    </li>
                </ul>
                <p class="adm-muted">
                    El troquel viene con {{ order.customization?.clearanceMm ?? 0.2 }} mm de holgura entre
                    piezas y {{ order.customization?.thicknessMm ?? 4 }} mm de grosor, en milímetros y listo
                    para laminar. El SVG está a escala real, con contornos cerrados y sin trazos.
                </p>
            </section>

            <section v-if="order.attachment" class="detalle__section">
                <h4 class="detalle__heading">
                    {{ order.customization ? 'Foto original del cliente' : 'Archivo adjunto' }}
                </h4>
                <p>
                    <strong>Nombre:</strong> {{ order.attachment.originalName }}<br />
                    <strong>Tipo:</strong> {{ order.attachment.mime || '—' }} —
                    <strong>Tamaño:</strong> {{ formatBytes(order.attachment.sizeBytes) }}
                </p>
                <AdmButton size="sm" :icon="Download" @click="onDownload">Descargar adjunto</AdmButton>
            </section>

            <section class="detalle__section">
                <h4 class="detalle__heading">Estado y notas</h4>
                <div class="detalle__form">
                    <AdmField v-model="form.status" label="Estado" type="select">
                        <option v-for="(meta, key) in statuses" :key="key" :value="key">{{ meta.label }}</option>
                    </AdmField>
                    <AdmField
                        v-model="form.adminNotes"
                        label="Notas internas"
                        type="textarea"
                        :rows="3"
                        maxlength="1000"
                    />
                </div>
            </section>

            <p class="detalle__meta adm-muted">
                Creada: {{ formatDate(order.createdAt) }} · Actualizada: {{ formatDate(order.updatedAt) }}
            </p>
        </div>

        <template #footer>
            <AdmButton variant="danger" :icon="Trash2" @click="onDelete">Eliminar solicitud</AdmButton>
            <AdmButton variant="ghost" @click="$emit('close')">Cerrar</AdmButton>
            <AdmButton :loading="saving" @click="onSaveStatus">Guardar cambios</AdmButton>
        </template>
    </AdmModal>
</template>

<script>
/**
 * Detalle de una solicitud 3D — migrado en el plan 27_09_2026 (F3/T18).
 *
 * El diálogo (backdrop, caja, cierre con Escape, foco) pasa a `AdmModal`;
 * antes traía los suyos propios. Los `alert()` pasan a avisos del panel y los
 * badges a `AdmBadge`. El contenido informativo y los cálculos no cambian.
 */
import { Download, Trash2 } from 'lucide-vue-next';
import {
    getOrderById, updateOrder, deleteOrder, downloadAttachment, downloadArtifact, fetchPreview,
} from '@/services/dbPrint3dAdmin.js';
import { formatNumberRAE, formatCurrencyRAE } from '@/utils/numberFormat';
import AdmModal from '@/components/Admin/ui/AdmModal.vue';
import AdmField from '@/components/Admin/ui/AdmField.vue';
import AdmButton from '@/components/Admin/ui/AdmButton.vue';
import AdmBadge, { ORDER_STATUS, REQUEST_TYPE } from '@/components/Admin/ui/AdmBadge.vue';
import { notify } from '@/composables/useAdmToast.js';

export default {
    name: 'OrdenDetalleModal',
    components: { AdmModal, AdmField, AdmButton, AdmBadge },
    props: {
        orderId: { type: [Number, String], required: true },
    },
    emits: ['close', 'updated', 'deleted'],
    data() {
        return {
            order: null,
            loading: true,
            saving: false,
            form: { status: '', adminNotes: '' },
            previewUrl: '',
            statuses: ORDER_STATUS,
            Download, Trash2,
        };
    },
    beforeUnmount() {
        if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
    },
    computed: {
        typeMeta() {
            return REQUEST_TYPE[this.order?.requestType] || { tone: 'neutral' };
        },
        /**
         * Archivos de producción con su resumen técnico. Lo que muestra son los
         * datos VERIFICADOS por el servidor (tamaño en disco) más los que declaró
         * el cliente al generarlos (`claimed`): triángulos, contornos y sha256.
         */
        artifacts() {
            const source = this.order?.productionArtifacts;
            if (!source) return [];

            const items = [];
            if (source.dieStl) {
                const claimed = source.dieStl.claimed || {};
                items.push({
                    kind: 'dieStl',
                    label: 'Troquel (STL)',
                    detail: [
                        this.formatBytes(source.dieStl.sizeBytes),
                        claimed.triangles ? `${formatNumberRAE(claimed.triangles, 0)} triángulos` : null,
                        claimed.cols && claimed.rows ? `rejilla ${claimed.cols} × ${claimed.rows}` : null,
                        claimed.droppedPieces ? `⚠ ${claimed.droppedPieces} pieza(s) ajustada(s)` : null,
                        claimed.sha256 ? `sha256 ${claimed.sha256.slice(0, 12)}…` : null,
                    ].filter(Boolean).join(' · '),
                });
            }
            if (source.imageSvg) {
                const claimed = source.imageSvg.claimed || {};
                items.push({
                    kind: 'imageSvg',
                    label: 'Imagen vectorizada (SVG)',
                    detail: [
                        this.formatBytes(source.imageSvg.sizeBytes),
                        claimed.paths ? `${formatNumberRAE(claimed.paths, 0)} contornos` : null,
                        claimed.points ? `${formatNumberRAE(claimed.points, 0)} puntos` : null,
                        claimed.dpi ? `${claimed.dpi} dpi` : null,
                        claimed.sha256 ? `sha256 ${claimed.sha256.slice(0, 12)}…` : null,
                    ].filter(Boolean).join(' · '),
                });
            }
            return items;
        },
    },
    methods: {
        async onDownloadArtifact(kind) {
            const { error } = await downloadArtifact(this.orderId, kind);
            if (error) notify.error(`Error al descargar: ${error}`);
        },
        async fetch() {
            this.loading = true;
            const { data } = await getOrderById(this.orderId);
            this.order = data;
            if (data) {
                this.form.status = data.status;
                this.form.adminNotes = data.adminNotes || '';
                // La previsualización exige sesión, así que no puede ir en el
                // src directamente: se descarga y se muestra como object URL.
                if (data.preview) {
                    const { url } = await fetchPreview(this.orderId);
                    if (url) this.previewUrl = url;
                }
            }
            this.loading = false;
        },
        async onSaveStatus() {
            this.saving = true;
            const { error } = await updateOrder(this.orderId, this.form);
            this.saving = false;
            if (error) {
                notify.error(`Error al guardar: ${error}`);
                return;
            }
            notify.success('Solicitud actualizada.');
            this.$emit('updated');
            this.fetch();
        },
        async onDelete() {
            if (!confirm(`¿Eliminar permanentemente la solicitud #${this.orderId}?\nEl archivo adjunto también será borrado del servidor.`)) return;
            const { error } = await deleteOrder(this.orderId);
            if (error) {
                notify.error(`Error al eliminar: ${error}`);
                return;
            }
            notify.success(`Solicitud #${this.orderId} eliminada.`);
            this.$emit('deleted');
            this.$emit('close');
        },
        async onDownload() {
            const { error } = await downloadAttachment(this.orderId, this.order.attachment.originalName);
            if (error) notify.error(`Error al descargar: ${error}`);
        },
        requestTypeLabel(type) {
            if (type === 'print_only') return 'Sólo impresión (con archivo 3D)';
            if (type === 'catalog_order') return 'Pedido del catálogo';
            if (type === 'custom_order') return 'Producto personalizado con foto';
            return 'Modelado + impresión';
        },
        productTypeLabel(tipo) {
            return tipo === 'estuche' ? 'Estuche' : 'Rompecabezas';
        },
        sizeLabel(sizeId) {
            const tamanos = {
                'puzzle-a5': 'A5 — 14.8 × 21 cm',
                'puzzle-a4': 'A4 — 21 × 29.7 cm',
                'case-cards': 'Estuche para tarjetas',
                'case-glasses': 'Estuche para gafas',
                'case-custom': 'Medida a convenir',
            };
            return tamanos[sizeId] || sizeId || '—';
        },
        caseLabel(opcion) {
            const opciones = {
                none: 'Sin estuche',
                carton: 'Estuche de cartón',
                print3d: 'Estuche impreso en 3D',
                matte: 'Acabado mate',
                glossy: 'Acabado brillante',
            };
            return opciones[opcion] || opcion || '—';
        },
        formatNumber(value, digits = 2) {
            return formatNumberRAE(value, digits);
        },
        formatCurrency(value) {
            return formatCurrencyRAE(value);
        },
        formatBytes(bytes) {
            if (!bytes) return '—';
            if (bytes < 1024) return `${bytes} B`;
            if (bytes < 1024 * 1024) return `${formatNumberRAE(bytes / 1024, 1)} KB`;
            return `${formatNumberRAE(bytes / 1024 / 1024, 2)} MB`;
        },
        formatDate(d) {
            if (!d) return '—';
            return new Date(d).toLocaleString('es-CO');
        },
    },
    mounted() { this.fetch(); },
};
</script>

<style scoped>
.detalle {
    display: flex;
    flex-direction: column;
    gap: var(--adm-gap-lg);
}

.detalle__section p {
    margin: 0.25rem 0;
    font-size: var(--adm-font-sm);
}

.detalle__heading {
    margin: 0 0 0.4rem 0;
    font-family: var(--font-subtitle);
    font-size: 0.8rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--color-text);
}

.detalle__product {
    display: flex;
    align-items: center;
    gap: var(--adm-gap);
}

.detalle__product img {
    width: 72px;
    height: 72px;
    object-fit: cover;
    border-radius: var(--adm-radius-sm);
    border: 1px solid var(--adm-border);
}

.detalle__description { white-space: pre-wrap; }

.detalle__section--pricing {
    padding: var(--adm-gap);
    background-color: var(--adm-surface-alt);
    border-radius: var(--adm-radius-sm);
}

.detalle__surcharge { color: var(--adm-warn-fg); }

.detalle__final {
    padding-top: 0.4rem;
    border-top: 1px solid var(--adm-border);
    font-size: var(--adm-font-base) !important;
}

.detalle__custom {
    display: flex;
    gap: var(--adm-gap);
    flex-wrap: wrap;
}

.detalle__preview { margin: 0; max-width: 220px; }

.detalle__preview img {
    width: 100%;
    border-radius: var(--adm-radius-sm);
    border: 1px solid var(--adm-border);
}

.detalle__custom-data {
    flex: 1;
    min-width: 220px;
    margin: 0;
    padding-left: 1.1rem;
    font-size: var(--adm-font-sm);
    line-height: 1.6;
}

.detalle__artifacts {
    list-style: none;
    margin: 0 0 var(--adm-gap-sm) 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--adm-gap-sm);
}

.detalle__artifacts li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--adm-gap);
    padding: 0.6rem 0.75rem;
    border: 1px solid var(--adm-border);
    border-radius: var(--adm-radius-sm);
}

.detalle__artifact-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.detalle__form {
    display: flex;
    flex-direction: column;
    gap: var(--adm-gap);
}

.detalle__meta {
    margin: 0;
    padding-top: var(--adm-gap-sm);
    border-top: 1px solid var(--adm-border);
}
</style>
